import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { TextField, InputAdornment, Tooltip, Button, Box, IconButton, Typography, MenuItem } from '@mui/material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, FileDownloadOutlined } from '@mui/icons-material';
import { getRepositoryBatchFiles } from '../../apis/repository';
import StatusBadge from '../StatusBadge';
import { useDebounce } from '../../hooks/useDebounce';
import { handleFileDownload } from '../../lib/utils';

const formatDate = (date) => {
    if (!date) return '-';

    return new Date(date).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const RepositoryBatchFileList = ({ batchId, pageSize = 10 }) => {
    const navigate = useNavigate();
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [totalCount, setTotalCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({
        pageSize,
        page: 0,
    });

    const debouncedSearch = useDebounce(search, 500);

    useEffect(() => {
        loadFiles();
    }, [batchId, paginationModel.page, paginationModel.pageSize, debouncedSearch, statusFilter]);

    const loadFiles = async () => {
        if (!batchId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await getRepositoryBatchFiles(
                batchId,
                paginationModel.page + 1,
                paginationModel.pageSize,
                search,
                statusFilter
            );
            const fileList = response.results || [];
            setFiles(fileList);
            setTotalCount(response.pagination.total_count);
        } catch (error) {
            // console.error('Error loading repository batch files:', error);
            toast.error('Failed to load repository batch files');
            setFiles([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            field: 'file_name',
            headerName: 'File Name',
            flex: 2,
            renderCell: (params) => params?.value || '-',
        },
        {
            field: 'started_at',
            headerName: 'Started',
            flex: 1.5,
            renderCell: (params) => formatDate(params?.row?.last_extraction?.started_at) || '-',
        },
        {
            field: 'finished_at',
            headerName: 'Finished',
            flex: 1.5,
            renderCell: (params) => formatDate(params?.row?.last_extraction?.finished_at) || '-',
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1.5,
            renderCell: (params) => {
                return (
                    <StatusBadge
                        status={
                            params?.row?.last_extraction == null
                                ? params?.value || '-'
                                : params?.row?.last_extraction?.status || '-'
                        }
                        type={params?.row?.last_extraction == null ? 'file' : 'extraction'}
                    />
                );
            },
        },
        {
            field: 'created_at',
            headerName: 'Created',
            flex: 1.5,
            valueFormatter: formatDate,
        },
        {
            field: 'remark',
            headerName: 'Remarks',
            flex: 2,
            renderCell: (params) => {
                const remark = params?.row?.last_extraction?.remark || params?.value || '-';

                const truncatedText = remark.length > 50 ? `${remark.substring(0, 50)}...` : remark;

                return (
                    <Tooltip title={remark} arrow placement="top">
                        {truncatedText}
                    </Tooltip>
                );
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.5,
            sortable: false,
            renderCell: (params) => {
                const download_link = params?.row?.last_extraction?.download_link ?? null;

                return (
                    <Tooltip title="Download File">
                        <IconButton
                            size="small"
                            disabled={download_link === null}
                            onClick={() => handleFileDownload(download_link)}
                        >
                            <FileDownloadOutlined color={download_link === null ? '#4C43A366' : 'primary'} />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
    ];

    return (
        <Box>
            <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate(-1)}
                sx={{ minWidth: 'auto', my: 1, border: 0 }}
            >
                Back
            </Button>

            <Box variant="div" className="custom_table_container">
                <Box variant="div" className="table_filters">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Typography variant="h6" fontWeight={600}>
                            {batchId && files?.length
                                ? `Batch - ${files?.[0]?.batch_details?.batch_name ?? ''}`
                                : 'All Files'}
                        </Typography>
                    </Box>
                    <Box className="filter_box">
                        <TextField
                            size="small"
                            placeholder="Search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <span className="material-symbols-rounded">search</span>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <TextField
                            select
                            label="Filter by status"
                            size="small"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{ minWidth: 180 }}
                        >
                            <MenuItem value="all">All Status</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="approved">Validated</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                            <MenuItem value="processing">Processing</MenuItem>
                        </TextField>
                    </Box>
                </Box>

                <Box variant="div" className="custom_table_responsive">
                    <DataGrid
                        rows={files}
                        columns={columns}
                        getRowId={(row) => row.id}
                        autoHeight
                        pageSizeOptions={[5, 10, 25, 50]}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pagination
                        paginationMode="server"
                        loading={loading}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        localeText={{
                            noRowsLabel: 'No Files Found',
                            sortLabel: '',
                        }}
                        rowCount={totalCount}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default RepositoryBatchFileList;
